export type ReviewOutcome = "approved" | "comment" | "changes_requested";

export type ReviewSeverity = "P1" | "P2" | "P3";
export type ReviewFindingStatus = "fixed" | "still_open" | "invalid";
export type ReviewRisk = {
  level: "low" | "medium" | "high";
  reasons: string[];
};

export interface ReviewFileComment {
  path: string;
  line: number;
  body: string;
  /** Absent on reports recorded before reviews rated severity. */
  severity?: ReviewSeverity;
  /** Absent on reports recorded before CI coverage was classified. */
  ci_covered?: boolean;
  /** Review agents that reported this finding; absent on older reports. */
  agents?: string[];
  /** The diff lines ending at `line`, cut at the reviewed head, e.g. `"@@ -4,2 +4,2 @@\n a\n+b"`. */
  excerpt?: string;
}

export interface ReviewPriorFinding {
  id: string;
  head_sha: string;
  path: string;
  line: number;
  body: string;
  severity: ReviewSeverity;
  /** Absent on findings tracked before CI coverage was classified. */
  ci_covered?: boolean;
  status: ReviewFindingStatus;
  reason: string;
}

export interface ReviewResult {
  outcome: ReviewOutcome;
  summary_comment: string;
  file_comments: ReviewFileComment[];
  prior_findings: ReviewPriorFinding[];
  risk?: ReviewRisk;
}

export type ReviewVerdictDecision = "approve" | "not_yet";
export type ReviewFindingDisposition = "fix" | "wont_fix" | "fast_follow";

export interface ReviewVerdictFinding {
  label: string;
  /** `new` points at `file_comments[index]`, `prior` at the prior finding whose id is `prior_id`. */
  kind: "new" | "prior";
  index: number | null;
  prior_id: string | null;
  path: string;
  line: number;
  severity: ReviewSeverity | null;
  disposition: ReviewFindingDisposition;
  reason: string | null;
}

export interface ReviewVerdict {
  verdict: ReviewVerdictDecision;
  decided_at: string;
  /** The round's auto-approval state, e.g. `"approved"` or `"would_approve"`; null while queued. */
  approval_decision: string | null;
  approval_skip_reason: string | null;
  findings: ReviewVerdictFinding[];
}

export interface ReviewReportMetadata {
  child_session_id: string;
  pr_url: string;
  pr_number: number;
  /** The PR head the review ran against; absent on reports mirrored before it was recorded. */
  head_sha?: string;
  review_result: ReviewResult;
  wake?: {
    skipped_reason: "round_cap" | "autofix_paused";
    managed_pr_id: string | null;
    max_rounds: number | null;
  };
  /** The lead agent's verdict on this round, once it records one. */
  verdict?: ReviewVerdict;
}

function isReviewOutcome(value: unknown): value is ReviewOutcome {
  return (
    value === "approved" || value === "comment" || value === "changes_requested"
  );
}

function isReviewSeverity(value: unknown): value is ReviewSeverity {
  return value === "P1" || value === "P2" || value === "P3";
}

function isReviewFindingStatus(value: unknown): value is ReviewFindingStatus {
  return value === "fixed" || value === "still_open" || value === "invalid";
}

function isReviewRisk(value: unknown): value is ReviewRisk {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const risk = value as Partial<ReviewRisk>;
  return (
    (risk.level === "low" ||
      risk.level === "medium" ||
      risk.level === "high") &&
    Array.isArray(risk.reasons) &&
    risk.reasons.every((reason) => typeof reason === "string")
  );
}

function isReviewFileComment(value: unknown): value is ReviewFileComment {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const comment = value as Partial<ReviewFileComment>;
  return (
    typeof comment.path === "string" &&
    typeof comment.line === "number" &&
    Number.isInteger(comment.line) &&
    comment.line > 0 &&
    typeof comment.body === "string" &&
    (comment.severity === undefined || isReviewSeverity(comment.severity)) &&
    (comment.ci_covered === undefined ||
      typeof comment.ci_covered === "boolean") &&
    (comment.excerpt === undefined || typeof comment.excerpt === "string")
  );
}

function isReviewAgents(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((agent) => typeof agent === "string" && agent.length > 0)
  );
}

function isReviewPriorFinding(value: unknown): value is ReviewPriorFinding {
  if (!isReviewFileComment(value)) return false;
  const finding = value as Partial<ReviewPriorFinding>;
  return (
    isReviewSeverity(finding.severity) &&
    typeof finding.id === "string" &&
    typeof finding.head_sha === "string" &&
    isReviewFindingStatus(finding.status) &&
    typeof finding.reason === "string"
  );
}

/** Reports recorded before finding tracking have no `prior_findings`; they read as an empty list. */
function parseReviewResult(value: unknown): ReviewResult | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const result = value as Partial<ReviewResult>;
  const priorFindings = result.prior_findings ?? [];
  if (
    !isReviewOutcome(result.outcome) ||
    typeof result.summary_comment !== "string" ||
    !Array.isArray(result.file_comments) ||
    !result.file_comments.every(isReviewFileComment) ||
    !Array.isArray(priorFindings) ||
    !priorFindings.every(isReviewPriorFinding)
  ) {
    return null;
  }
  const fileComments = result.file_comments.map((value) => {
    const comment = value as ReviewFileComment & { agents?: unknown };
    const { agents, ...fields } = comment;
    return {
      ...fields,
      ...(isReviewAgents(agents) ? { agents } : {}),
    };
  });
  return {
    outcome: result.outcome,
    summary_comment: result.summary_comment,
    file_comments: fileComments,
    prior_findings: priorFindings,
    ...(isReviewRisk(result.risk) ? { risk: result.risk } : {}),
  };
}

export type ReviewWake = NonNullable<ReviewReportMetadata["wake"]>;

function isReviewWake(value: unknown): value is ReviewWake {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const wake = value as Partial<ReviewWake>;
  return (
    (wake.skipped_reason === "round_cap" ||
      wake.skipped_reason === "autofix_paused") &&
    (typeof wake.managed_pr_id === "string" || wake.managed_pr_id === null) &&
    (wake.max_rounds === null ||
      (typeof wake.max_rounds === "number" &&
        Number.isInteger(wake.max_rounds) &&
        wake.max_rounds > 0))
  );
}

function isStringOrNull(value: unknown): value is string | null {
  return typeof value === "string" || value === null;
}

function isReviewFindingDisposition(
  value: unknown,
): value is ReviewFindingDisposition {
  return value === "fix" || value === "wont_fix" || value === "fast_follow";
}

function isReviewVerdictFinding(value: unknown): value is ReviewVerdictFinding {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const finding = value as Partial<ReviewVerdictFinding>;
  const pointsAtTarget =
    finding.kind === "new"
      ? typeof finding.index === "number" &&
        Number.isInteger(finding.index) &&
        finding.index >= 0
      : finding.kind === "prior" && typeof finding.prior_id === "string";
  return (
    pointsAtTarget &&
    typeof finding.label === "string" &&
    (finding.index === null || typeof finding.index === "number") &&
    isStringOrNull(finding.prior_id) &&
    typeof finding.path === "string" &&
    typeof finding.line === "number" &&
    Number.isInteger(finding.line) &&
    (finding.severity === null || isReviewSeverity(finding.severity)) &&
    isReviewFindingDisposition(finding.disposition) &&
    isStringOrNull(finding.reason)
  );
}

function isReviewVerdict(value: unknown): value is ReviewVerdict {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const verdict = value as Partial<ReviewVerdict>;
  return (
    (verdict.verdict === "approve" || verdict.verdict === "not_yet") &&
    typeof verdict.decided_at === "string" &&
    isStringOrNull(verdict.approval_decision) &&
    isStringOrNull(verdict.approval_skip_reason) &&
    Array.isArray(verdict.findings) &&
    verdict.findings.every(isReviewVerdictFinding)
  );
}

/** Accepts anything shaped like the mirrored `review_report` metadata; null when a field is missing or malformed. A malformed `verdict` is dropped. */
export function parseReviewReportMetadata(
  raw: object,
): ReviewReportMetadata | null {
  const report = raw as Partial<ReviewReportMetadata>;
  const reviewResult = parseReviewResult(report.review_result);
  if (
    typeof report.child_session_id !== "string" ||
    typeof report.pr_url !== "string" ||
    typeof report.pr_number !== "number" ||
    !Number.isInteger(report.pr_number) ||
    reviewResult === null ||
    (report.head_sha !== undefined && typeof report.head_sha !== "string") ||
    (report.wake !== undefined && !isReviewWake(report.wake))
  ) {
    return null;
  }
  return {
    child_session_id: report.child_session_id,
    pr_url: report.pr_url,
    pr_number: report.pr_number,
    ...(report.head_sha ? { head_sha: report.head_sha } : {}),
    review_result: reviewResult,
    ...(report.wake ? { wake: report.wake } : {}),
    ...(isReviewVerdict(report.verdict) ? { verdict: report.verdict } : {}),
  };
}

// One parse per raw object, so a report keeps its identity across renders
// and index rebuilds can tell "unchanged" from "changed" by reference.
const parsedByRaw = new WeakMap<object, ReviewReportMetadata | null>();

export function reviewReportMetadata(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): ReviewReportMetadata | null {
  if (message.role !== "agent") return null;
  const raw = message.metadata?.review_report;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  let parsed = parsedByRaw.get(raw);
  if (parsed === undefined) {
    parsed = parseReviewReportMetadata(raw);
    parsedByRaw.set(raw, parsed);
  }
  return parsed;
}

export function isReviewReportMessage(message: {
  role: string;
  metadata?: Record<string, unknown> | null;
}): boolean {
  return reviewReportMetadata(message) !== null;
}
